import React, { useState, useEffect, useRef } from 'react';
import { getBankAccounts, createTransaction } from '../services/api';
import { showSuccess, showError } from './ToastNotifications';
import { formatCurrency, clampDecimals, sanitizeAmountInput, amountInputGuard, resolveUploadUrl } from '../utils/helpers';
import { prReceipts, prProgressText, prRecordedName, prLiveCardTitle, prReceiptRecordText } from '../utils/paymentTask';
import './CreatePRModal.css';

// ── THE ONE "record a payment received" writer ───────────────────────────────
//
// There used to be two components posting the same credit to the same endpoint
// (POST /api/bank-accounts/:id/transactions) with different forms, different
// wording and — the part that mattered — different safeguards: CreatePRModal
// (finance's dashboard button) had an account grid, a remembered last-used
// account and NO confirmation step; RecordPaymentReceivedModal (the task path)
// had a plain dropdown, a confirmation dialog and the receipt panel. The audit
// flagged the pair as duplicate-write surface #6. This file is the union: ONE
// component, ONE request shape, ONE confirm text, with `task` deciding the mode.
//
// TASK MODE (`task` given) — finance records the credit for a sales-submitted
// receipt (a `payment_received` pending_task). The request carries
// `payment_received_task_id`, so the server links + completes the task inside
// the same DB transaction, files the receipt against the transaction and
// notifies the submitting sales user. It does NOT carry a customer_id: the
// customer link belongs to the SUBMITTING SALES USER and the server takes the
// attribution from the task — a client-supplied customer_id is REJECTED (400)
// rather than silently ignored, so this modal must never send one.
//
// CREATE MODE (`task` null) — finance records a credit straight off the bank
// statement with no receipt behind it. No task id, no customer: the credit
// lands unattributed (the "Unattributed" bucket, NAV-neutral) and is assignable later
// through the normal picker, which is exactly the state a pr_notification then
// nags about.
//
// No forked recorder either way: both post to the EXISTING bank add-transaction
// endpoint, so bank balance, ledger row, reference number, checkpoint and
// consumed-set behaviour are identical by construction.
//
// MULTI-RECEIPT TASK MODE (2026-08): a payment_received task can carry 1..20
// receipts (operator: "the sales has to specify how much is the receipt
// worth, and finance has to create 5 x PR under this one order"). The single
// `task.request_proof_url` image panel is replaced by a GALLERY over
// `prReceipts(task)` — the ONE parser, shared with the pool card and Task
// Tracking, so all three surfaces can never disagree about how many slips
// exist or which ones are recorded. The gallery is seeded into local state
// (`receipts`) ONCE on open and never re-derived from a fresh `task` prop
// afterwards: a background poll refreshing the host's task list must NEVER
// tear this open modal out from under finance mid-recording (Phase-6 rule,
// docs/denorm-campaign-2026-07.md) — the modal patches its OWN copy from each
// record response instead (see handleSubmit's partial branch).
export default function RecordPaymentReceivedModal({ isOpen, task, onClose, onSuccess, onPartial }) {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  // The frozen receipt SET (see the header note) and which PENDING slip
  // finance currently has selected to record. `null` = nothing selected yet
  // (either no task, or 2+ slips still pending and none has been chosen).
  const [receipts, setReceipts] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  // "Focuses the account picker" (spec) once a slip is picked — the picker is
  // a button grid, not a native input, so there is nothing meaningful to call
  // .focus() on; scrolling it into view is the equivalent nudge.
  const accountSectionRef = useRef(null);

  // Task mode is rendered conditionally by its hosts (`{recordPrTask && <… />}`)
  // and passes no isOpen; create mode drives it with a boolean. Undefined = open.
  const open = isOpen !== false;
  const isTaskMode = !!task;

  useEffect(() => {
    if (!open) return;
    // Seed the frozen receipt set ONCE from the task prop (see header note).
    const seeded = prReceipts(task);
    setReceipts(seeded);
    const pendingIdxs = seeded
      .map((r, i) => (r.bank_transaction_id == null ? i : -1))
      .filter((i) => i !== -1);
    // Auto-select ONLY when exactly one slip is still pending — covers the
    // common single-receipt task (today's flow, no extra click added) and the
    // tail of a multi where just the last slip remains. With two or more
    // pending, force the explicit "Record this receipt" click below: this app
    // never guesses which slip a credit is for (the server's RECEIPT_REQUIRED
    // 400 enforces the same rule; this just keeps finance from having to hit
    // it by defaulting to a wrong guess).
    const autoIdx = pendingIdxs.length === 1 ? pendingIdxs[0] : null;
    setActiveIndex(autoIdx);
    // Amount and description start BLANK (operator 2026-10-14): finance types
    // what they actually see credited in the bank, never confirms a number
    // sales wrote. The slip card still shows "stated RM x" for reference.
    setAmount('');
    setDescription('');
    setAccountId('');
    setErrors({});
    setLoading(true);
    getBankAccounts()
      // The endpoint returns an OBJECT {accounts, bankAccounts, ...}, not an
      // array — treating it as one made .filter throw and the catch left the
      // dropdown empty, wedging finance on this modal.
      .then((data) => {
        const rows = Array.isArray(data) ? data : (data && data.accounts) || [];
        const usable = rows.filter(
          (a) => ['bank', 'cash'].includes(String(a.account_type || 'bank'))
        );
        setAccounts(usable);
        // Remembered last-used account — CREATE MODE ONLY. In task mode finance
        // must positively pick the account they can SEE the credit in; a
        // pre-selected one is exactly how the wrong account gets recorded.
        if (!task) {
          const last = localStorage.getItem('lastPRAccountId');
          if (last && usable.some((a) => String(a.id) === String(last))) setAccountId(last);
          else if (last) localStorage.removeItem('lastPRAccountId');
        }
      })
      .catch(() => setAccounts([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, open]);

  if (!open) return null;
  if (isOpen === undefined && !task) return null; // legacy task-only mount, nothing to show

  const selectedAccount = accounts.find((a) => String(a.id) === String(accountId)) || null;
  const accountLabel = selectedAccount
    ? `${selectedAccount.bank_name} - ${selectedAccount.account_name}`
    : 'the selected account';

  // Local-only progress (never re-derived from `task`, per the header note —
  // `receipts` IS the frozen set, patched in place as each slip is recorded).
  const totalReceipts = receipts.length;
  const recordedCount = receipts.filter((r) => r.bank_transaction_id != null).length;
  const isMulti = totalReceipts > 1;
  const activeReceipt = activeIndex != null ? receipts[activeIndex] : null;
  // The shared formatter (utils/paymentTask) takes a task-shaped object; a
  // tiny synthetic wrapper around the frozen local array lets this modal print
  // the BYTE-IDENTICAL progress text the pool card and Task Tracking show,
  // without a second copy of the "RM x of RM y (n of m)" formatting.
  const progressText = () => prProgressText({ task_type: 'payment_received', pr_receipts_json: JSON.stringify(receipts) });

  const selectReceipt = (idx) => {
    setActiveIndex(idx);
    setAmount(''); // blank on purpose — finance types the credited amount (see the open effect)
    setAccountId('');
    setErrors({});
    if (accountSectionRef.current) {
      accountSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const validate = (amt) => {
    const e = {};
    // Only reachable with 2+ slips still pending (the single-pending case
    // auto-selects on open) — but stay defensive rather than trust that.
    if (isTaskMode && totalReceipts > 0 && activeIndex == null) {
      e.receipt = 'Pick which receipt you are recording — click "Record this receipt" on the slip you can see credited.';
    }
    if (!accountId) e.accountId = 'Pick the bank/cash account the money was credited into';
    if (!amount || isNaN(amt) || amt <= 0) e.amount = 'Enter the amount that was received';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ONE confirm text, mode-aware. Create mode never asked before — recording a
  // credit moves the bank balance and fires a checkpoint, so it asks now too.
  const confirmText = (amt) => {
    const lines = [
      `Record ${formatCurrency(amt)} received into ${accountLabel}?`,
      '',
      'Only do this once you can see the credit in the account.',
    ];
    if (isTaskMode) {
      lines.push(task.customer_id
        ? `• it will be attributed to ${task.customer_name || 'the linked customer'}`
        : '• it stays unattributed (assignable later) — no customer was linked');
      lines.push('• the receipt sales uploaded is linked to this transaction');
      // MULTI-RECEIPT: say exactly which slip this is and whether the task
      // stays open or closes — the point of no return is where finance needs
      // to know this, not discovered afterwards from a toast.
      if (isMulti) {
        const position = activeIndex != null ? activeIndex + 1 : recordedCount + 1;
        const isLast = recordedCount + 1 >= totalReceipts;
        lines.push(isLast
          ? '• this is the LAST receipt — the task completes and sales is notified'
          : `• this records receipt ${position} of ${totalReceipts} — the task stays open until all ${totalReceipts} are recorded`);
      } else {
        lines.push('• sales is notified with this description and amount');
      }
    } else {
      lines.push('• it stays unattributed (assignable later) — assign it to a customer from the transaction picker');
      lines.push('• sales is alerted that a payment was received');
    }
    return lines.join('\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!validate(amt)) return;
    // Notes are OPTIONAL (operator rule): default to the task's own wording so
    // the sales notification always carries something meaningful; with no task
    // there is nothing to fall back to but the legacy 'Payment Received'.
    // Prefer the LIVE source-row text over the frozen task copy — this string
    // becomes the bank transaction's permanent description (task-mirror audit
    // 2026-08-13: the copy is the sales-submitted wording at insert time; a
    // corrected payer name must not be laundered back into a new record).
    const desc = description.trim()
      || (isTaskMode ? (prRecordedName(task) || task.description || task.title || 'Payment received') : 'Payment Received');
    if (!window.confirm(confirmText(amt))) return;
    setSubmitting(true);
    setErrors({});
    try {
      const resp = await createTransaction(accountId, {
        transaction_type: 'credit',
        amount: amt,
        description: desc,
        // NO customer_id in either mode — see the header note.
        ...(isTaskMode ? { payment_received_task_id: task.id } : {}),
        // A `null` id (the legacy single-receipt fallback shape prReceipts()
        // synthesizes) is POSTED WITHOUT this key — never invented — which is
        // what keeps the server on the byte-identical 0/1-slip code path.
        ...(isTaskMode && activeReceipt && activeReceipt.id != null
          ? { payment_received_receipt_id: activeReceipt.id } : {}),
      });
      if (isTaskMode) {
        // `payment_received_task_completed` only exists once the backend
        // carries this feature; its absence (older server, or a task that
        // never had more than one slip) means "today's single-record
        // completion" — undefined must default to COMPLETE, never partial.
        const completed = resp?.payment_received_task_completed !== false;
        if (completed) {
          showSuccess('Payment received recorded — sales has been notified');
          if (onSuccess) onSuccess();
          onClose();
        } else {
          // PARTIAL: slips remain. Patch OUR OWN copy of the just-recorded
          // slip in place — never re-derive `receipts` from a fresh `task`
          // prop, so a background poll on the host can never tear this open
          // modal (Phase-6 rule) — then hand control back to the gallery for
          // the next slip instead of closing.
          const recordedNow = resp?.payment_received_recorded_count ?? (recordedCount + 1);
          const totalNow = resp?.payment_received_receipt_count ?? totalReceipts;
          showSuccess(`Receipt recorded — ${formatCurrency(amt)}. ${recordedNow} of ${totalNow} receipts recorded so far.`);
          setReceipts((prev) => prev.map((r, i) => (i === activeIndex ? {
            ...r,
            bank_transaction_id: resp.id,
            recorded_amount: resp?.amount ?? amt,
            recorded_description: resp?.description ?? desc,
            recorded_account_label: accountLabel,
            recorded_at: new Date().toISOString(),
            // NOT in the create-transaction response (the reference number is
            // DB-trigger assigned); the host's onPartial silent refetch fills
            // this in for real off the next pr_receipts_json payload.
            recorded_reference_no: r.recorded_reference_no ?? null,
          } : r)));
          setActiveIndex(null);
          setAmount('');
          setAccountId('');
          setErrors({});
          if (onPartial) onPartial();
        }
      } else {
        showSuccess('Payment Received recorded successfully!');
        if (onSuccess) onSuccess();
        // Create mode records several PRs in a row: remember the account, clear
        // the figures, and close on a short delay so the toast is readable.
        localStorage.setItem('lastPRAccountId', String(accountId));
        setAmount('');
        setDescription('');
        setTimeout(() => onClose(), 500);
      }
    } catch (err) {
      const msg = err.response?.data?.error
        || (isTaskMode ? 'Failed to record the payment' : 'Failed to record payment received');
      showError(msg);
      setErrors({ submit: msg });
      // 409 = someone already recorded this receipt; refetch so the pool is honest.
      if (err.response?.status === 409) { if (onSuccess) onSuccess(); onClose(); }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => { if (!submitting) onClose(); };

  return (
    <div className="modal-overlay" onClick={() => isTaskMode && handleClose()}>
      <div className="modal-content create-pr-modal" style={{ maxWidth: isTaskMode ? 640 : 500 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isTaskMode ? 'Confirm Payment Received' : 'Create PR (Payment Received)'}</h3>
          <button className="close-button" onClick={handleClose} disabled={submitting}>&times;</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="loading-message">Loading bank accounts...</div>
          ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 260px' }}>
                {isTaskMode && (
                  <div className="form-group">
                    <label className="form-label">Task</label>
                    <div style={{ fontSize: '0.9rem' }}>
                      <strong>{prLiveCardTitle(task) || task.title}</strong>
                      {task.amount != null && task.amount !== '' && (
                        <span> · stated {formatCurrency(task.amount)}</span>
                      )}
                    </div>
                  </div>
                )}

                <div className="form-group" ref={accountSectionRef}>
                  <label className="form-label">
                    {isTaskMode ? 'Received into' : 'Bank Account'} <span className="required">*</span>
                  </label>
                  {/* ONE account picker for both entry points: full-width
                      buttons showing each account's live balance, so the
                      operator picks the account they can SEE the money in. */}
                  <div className="account-buttons-grid">
                    {accounts.map((account) => (
                      <button
                        key={account.id}
                        type="button"
                        className={`account-btn ${String(accountId) === String(account.id) ? 'selected' : ''}`}
                        onClick={() => setAccountId(String(account.id))}
                        disabled={submitting}
                      >
                        <span className="account-bank">{account.bank_name}</span>
                        <span className="account-name">{account.account_name}</span>
                        <span className="account-balance">{formatCurrency(account.current_balance)}</span>
                      </button>
                    ))}
                  </div>
                  {errors.accountId && <span className="error-message">{errors.accountId}</span>}
                  {isTaskMode && (
                    <small className="form-text">Pick the account you can SEE the customer's payment credited in.</small>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="pr-amount">
                    {isTaskMode ? 'Amount received (MYR)' : 'Amount (MYR)'} <span className="required">*</span>
                  </label>
                  <input
                    id="pr-amount"
                    type="number"
                    className={`form-control ${errors.amount ? 'error' : ''}`}
                    step="0.01"
                    min="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(clampDecimals(sanitizeAmountInput(e.target.value), 2))}
                    {...amountInputGuard}
                    disabled={submitting}
                  />
                  {errors.amount && <span className="error-message">{errors.amount}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="pr-description">Description{isTaskMode ? ' (optional)' : ''}</label>
                  <input
                    id="pr-description"
                    type="text"
                    className="form-control"
                    value={description}
                    maxLength={200}
                    placeholder={isTaskMode ? 'e.g., payer name as shown on the bank statement' : 'e.g., Payment from customer, Bank transfer'}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={submitting}
                  />
                  <small className="form-text">
                    {isTaskMode
                      ? "Optional — type what the bank statement shows. If left blank, the task's note is used. Shown on the transaction history and in the notification sales receives."
                      : 'Optional: type what the bank statement shows — this is the text every later screen reads.'}
                  </small>
                </div>

                {isTaskMode && (task.customer_id ? (
                  <small className="form-text">Will be attributed to <strong>{task.customer_name || 'the linked customer'}</strong> in the same booking.</small>
                ) : (
                  <small className="form-text">No customer linked — the credit stays unattributed (assignable later).</small>
                ))}
              </div>

              {/* RECEIPT GALLERY — replaces the old single task.request_proof_url
                  panel. `receipts` is the FROZEN local copy (see header note);
                  every entry is either RECORDED (green, shows what finance
                  actually booked) or PENDING ("Record this receipt" selects
                  it — prefills the amount from stated_amount and scrolls the
                  account picker into view). The amount input stays editable
                  even once prefilled: what finance can see in the bank is
                  what gets recorded, and a divergence from stated_amount must
                  be surfaced, never prevented. */}
              {isTaskMode && receipts.length > 0 && (
                <div style={{ flex: '1 1 240px' }}>
                  <label className="form-label">
                    {isMulti ? `Receipts (${recordedCount} of ${totalReceipts} recorded)` : 'Receipt from sales'}
                  </label>
                  {isMulti && (
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                      {progressText()}
                    </div>
                  )}
                  {errors.receipt && <div className="alert alert-error" style={{ marginBottom: 8 }}>{errors.receipt}</div>}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 380, overflowY: 'auto', paddingRight: 2 }}>
                    {receipts.map((r, i) => {
                      const recorded = r.bank_transaction_id != null;
                      const isActive = activeIndex === i;
                      return (
                        <div
                          key={r.id ?? `legacy-${i}`}
                          style={{
                            display: 'flex', gap: 8, padding: 8, borderRadius: 6,
                            border: isActive ? '2px solid #2563eb' : '1px solid #e5e7eb',
                            background: recorded ? '#f0fdf4' : '#fff',
                          }}
                        >
                          <a href={resolveUploadUrl(r.url)} target="_blank" rel="noopener noreferrer" title="Click for full size">
                            <img src={resolveUploadUrl(r.url)} alt={isMulti ? `Receipt ${i + 1} of ${totalReceipts}` : "Customer's payment receipt"}
                              style={{ width: 76, height: 76, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb', background: '#f9fafb', display: 'block', flex: '0 0 auto' }} />
                          </a>
                          <div style={{ flex: 1, fontSize: 12, minWidth: 0 }}>
                            {isMulti && <div style={{ fontWeight: 700 }}>Receipt {i + 1} of {totalReceipts}</div>}
                            <div>stated {formatCurrency(r.stated_amount)}</div>
                            {recorded ? (
                              <>
                                <span style={{ display: 'inline-block', marginTop: 4, fontSize: 11, fontWeight: 700, color: '#166534', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 4, padding: '1px 6px' }}>RECORDED ✓</span>
                                <div style={{ marginTop: 4, color: '#374151' }}>{prReceiptRecordText(r)}</div>
                                {(r.recorded_account_label || r.recorded_reference_no) && (
                                  <div style={{ color: '#6b7280' }}>
                                    {r.recorded_account_label}
                                    {r.recorded_account_label && r.recorded_reference_no ? ' · ' : ''}
                                    {r.recorded_reference_no}
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <span style={{ display: 'inline-block', marginTop: 4, fontSize: 11, fontWeight: 700, color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 4, padding: '1px 6px' }}>PENDING</span>
                                <div style={{ marginTop: 4 }}>
                                  <button
                                    type="button"
                                    className="btn btn-small btn-outline"
                                    disabled={submitting}
                                    onClick={() => selectReceipt(i)}
                                  >
                                    {isActive ? 'Selected' : 'Record this receipt'}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {errors.submit && <div className="alert alert-error">{errors.submit}</div>}

            <div className="form-actions" style={{ marginTop: 12 }}>
              <button type="submit" className={isTaskMode ? 'btn btn-primary' : 'btn btn-success'} disabled={submitting}>
                {submitting
                  ? (isTaskMode ? 'Recording…' : 'Recording...')
                  : (isTaskMode ? 'Confirm payment received' : 'Record Payment Received')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={submitting}>
                {isTaskMode ? 'Close' : 'Cancel'}
              </button>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
